// components/sales/sections/GSCPerformanceChart.jsx

import { memo, useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import useGSCTopPerformers from "../../../hooks/sales/useGSCTopPerformers";
import { getChartColors } from '../../../theme/colors';

export const GSCPerformanceChart = memo(({ dateRange }) => {
  const [dimensionType, setDimensionType] = useState('query'); // 'query' or 'page'

  const { 
    chartData, 
    topPerformer, 
    totalClicks,
    avgCTR,
    period,
    loading, 
    error 
  } = useGSCTopPerformers(dimensionType, dateRange, 10);

  const colors = useMemo(() => {
    return getChartColors(['brightblue']); // Use brand orange
  }, []);

  const barColor = colors[0];

  // Custom tooltip showing all GSC metrics
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-900 text-white p-4 rounded-lg shadow-xl text-sm min-w-[280px]">
          <p className="font-bold text-base mb-3 pb-2 border-b border-gray-700 break-words">
            {data.fullValue || data.name}
          </p>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-300">Clicks:</span>
              <span className="font-semibold text-white">
                {data.clicks.toLocaleString()}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-300">Impressions:</span>
              <span className="font-semibold text-blue-400">
                {data.impressions.toLocaleString()}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-300">CTR:</span>
              <span className="font-semibold text-green-400">
                {data.ctr}%
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-300">Avg Position:</span>
              <span className="font-semibold text-yellow-400">
                {data.position}
              </span>
            </div>
          </div>
          
          <div className="mt-3 pt-2 border-t border-gray-700">
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-xs">% of Total Clicks:</span>
              <span className="font-semibold text-white">
                {data.percentage}%
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="h-6 bg-gray-200 rounded w-64 mb-4 animate-pulse"></div>
        <div className="h-[300px] bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Search Console Performance
        </h3>
        <div className="h-[300px] bg-red-50 rounded border border-red-200 flex items-center justify-center">
          <div className="text-red-600 text-center">
            <p className="font-semibold">Error loading GSC data</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (!chartData || chartData.length === 0) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Search Console Performance
        </h3>
        <div className="h-[300px] bg-gray-50 rounded border border-gray-200 flex items-center justify-center">
          <div className="text-gray-500 text-center">
            <p className="font-semibold">No search console data available</p>
            <p className="text-sm mt-1">Try selecting a different date range</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Organic Search Performance
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Top {dimensionType === 'query' ? 'search queries' : 'landing pages'} by clicks
            </p>
            {period && (
              <p className="text-xs text-gray-400 mt-1">
                {new Date(period.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} to{' '}
                {new Date(period.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            )}
          </div>
          
          {/* Toggle between Queries and Pages */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setDimensionType('query')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                dimensionType === 'query'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Queries
            </button>
            <button
              onClick={() => setDimensionType('page')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                dimensionType === 'page'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Pages
            </button>
          </div>
        </div>
        
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="text-gray-600">
              <span className="font-semibold">{totalClicks.toLocaleString()}</span> total clicks
            </div>
            <div className="text-gray-600">
              <span className="font-semibold">{avgCTR}%</span> avg CTR
            </div>
          </div>
        </div>
      </div>
      
      <div style={{ height: "300px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={chartData} 
            layout="vertical"
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={true} vertical={false} />
            <XAxis 
              type="number"
              tick={{ fontSize: 12 }}
              stroke="#6b7280"
              label={{ 
                value: 'Clicks', 
                position: 'insideBottom',
                offset: -5,
                style: { fontSize: 12, fill: '#6b7280' }
              }}
            />
            <YAxis 
              type="category"
              dataKey="name" 
              tick={{ fontSize: 11 }}
              width={dimensionType === 'page' ? 180 : 150}
              stroke="#6b7280"
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(249, 115, 22, 0.1)' }} />
            <Bar 
              dataKey="clicks" 
              radius={[0, 8, 8, 0]}
              maxBarSize={50}
            >
              {chartData.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={barColor}
                  opacity={1 - (index * 0.03)} // Slight gradient effect
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});

export default GSCPerformanceChart;